import { Box, Tab, Tabs } from '@mui/material';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import {
  JobAssignStatus,
  JobQueueTableType,
} from 'pages/iPriseV2/common/enum/TeamUserRoles';
import CommonJobQueueTable from 'pages/iPriseV2/common/JobQueueTable';
import React, { useCallback } from 'react';

import ColumnsSidebar from './ColumnsSidebar';

const JobQueueTable: React.FC = () => {
  const { managementJobQueueService } = useStore();
  const {
    jobSubTab,
    unAllocatedSubTab,
    unAllocatedPage,
    unAllocatedRowsPerPage,
    recruiterPage,
    recruiterRowsPerPage,
    teamLeadPage,
    teamLeadRowsPerPage,
    supportPage,
    supportRowsPerPage,
    submittedPage,
    submittedRowsPerPage,
    selectedTeam,
    selectedSort,
    searchTerm,
    percentage,
  } = managementJobQueueService;

  const handleJobSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managementJobQueueService.setJobSubTab(newValue);
    },
    [managementJobQueueService],
  );

  const handleUnAllocatedSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managementJobQueueService.setUnAllocatedSubTab(newValue);
    },
    [managementJobQueueService],
  );

  return (
    <Box className="table-container-with-sidebar">
      <Box className="table-main-content">
        <Box className="sub-tabs-container">
          <Box className="sub-tabs-wrapper">
            <Tabs
              value={jobSubTab}
              onChange={handleJobSubTabChange}
              className="sub-tabs"
              variant="scrollable"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                className: 'sub-tab-indicator',
              }}>
              <Tab
                label={
                  <Box className="tab-label-with-badge">
                    <span className="tab-dot" />
                    Unallocated
                  </Box>
                }
                className="sub-tab"
              />
              <Tab label="Recruiter Queue" className="sub-tab" />
              <Tab label="Team Lead Queue" className="sub-tab" />
              <Tab label="Support Queue" className="sub-tab" />
              <Tab label="Submitted" className="sub-tab" />
            </Tabs>
          </Box>
        </Box>

        {jobSubTab === 0 && (
          <Box className="sub-tabs-container">
            <Box className="sub-tabs-wrapper">
              <Tabs
                value={unAllocatedSubTab}
                onChange={handleUnAllocatedSubTabChange}
                className="sub-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  className: 'sub-tab-indicator',
                }}>
                <Tab label="TL Unallocated" className="sub-tab" />
                <Tab label="Recruiter Unallocated" className="sub-tab" />
              </Tabs>
            </Box>
          </Box>
        )}

        {jobSubTab === 0 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.MANAGEMENT_JQ_UNALLOCATED}
            status={
              unAllocatedSubTab === 0
                ? JobAssignStatus.TL_UNALLOCATED
                : JobAssignStatus.RECRUITER_UNALLOCATED
            }
            queryKey="job-postings-unallocated"
            page={unAllocatedPage}
            rowsPerPage={unAllocatedRowsPerPage}
            onPageChange={(p) =>
              managementJobQueueService.setUnAllocatedPage(p)
            }
            onRowsPerPageChange={(r) =>
              managementJobQueueService.setUnAllocatedRowsPerPage(r)
            }
            popperId="skills-popper-unallocated"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 1 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.MANAGEMENT_JQ_RECRUITER}
            status={`${JobAssignStatus.RECRUITER_ASSIGNED},${JobAssignStatus.RECRUITER_ACCEPTED}`}
            queryKey="job-postings-recruiter-queue"
            page={recruiterPage}
            rowsPerPage={recruiterRowsPerPage}
            onPageChange={(p) => managementJobQueueService.setRecruiterPage(p)}
            onRowsPerPageChange={(r) =>
              managementJobQueueService.setRecruiterRowsPerPage(r)
            }
            popperId="skills-popper-recruiter"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 2 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.MANAGEMENT_JQ_TEAM_LEAD}
            status={JobAssignStatus.TL_ASSIGNED}
            queryKey="job-postings-team-lead-queue"
            page={teamLeadPage}
            rowsPerPage={teamLeadRowsPerPage}
            onPageChange={(p) => managementJobQueueService.setTeamLeadPage(p)}
            onRowsPerPageChange={(r) =>
              managementJobQueueService.setTeamLeadRowsPerPage(r)
            }
            popperId="skills-popper-team-lead"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 3 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.MANAGEMENT_JQ_SUPPORT}
            status={JobAssignStatus.TL_APPROVED}
            queryKey="job-postings-support-queue"
            page={supportPage}
            rowsPerPage={supportRowsPerPage}
            onPageChange={(p) => managementJobQueueService.setSupportPage(p)}
            onRowsPerPageChange={(r) =>
              managementJobQueueService.setSupportRowsPerPage(r)
            }
            popperId="skills-popper-support"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 4 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.MANAGEMENT_JQ_SUBMITTED}
            status={JobAssignStatus.SUBMITTED}
            queryKey="job-postings-submitted-queue"
            page={submittedPage}
            rowsPerPage={submittedRowsPerPage}
            onPageChange={(p) => managementJobQueueService.setSubmittedPage(p)}
            onRowsPerPageChange={(r) =>
              managementJobQueueService.setSubmittedRowsPerPage(r)
            }
            popperId="skills-popper-submitted"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
      </Box>
      <ColumnsSidebar />
    </Box>
  );
};

export default observer(JobQueueTable);
